import styled, {css, DefaultTheme} from 'styled-components'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AgendaEventStatus} from '@/features/agenda/types'
import {agendaEventStatusMapToBackgroundColor, agendaEventStatusMapToColor} from '@/features/agenda/utils'

export const makeAgendaAppointmentBackground = (theme: DefaultTheme) => {
    const defaultBehaviourStatuses = [
        'scheduled',
        'completed',
        'missed',
        'cancelled',
        'withdrawn',
        'tentative'
    ] as const

    return {
        ...defaultBehaviourStatuses.reduce(
            (acc, status) => {
                acc[status] = css`
                    background-color: ${agendaEventStatusMapToBackgroundColor[status]};
                `
                return acc
            },
            {} as Record<AgendaEventStatus, ReturnType<typeof css>>
        ),
        provisional: css`
            background-color: ${theme.palette.neutral['white']};
            background-image: repeating-linear-gradient(
                45deg,
                ${theme.palette.orange['50']} 0px,
                ${theme.palette.orange['50']} 10px,
                transparent 10px,
                transparent 20px
            );
        `,
        to_reschedule: css`
            background-color: ${theme.palette.neutral['white']};
            background-image: repeating-linear-gradient(
                45deg,
                ${theme.palette.primary['50']} 0px,
                ${theme.palette.primary['50']} 10px,
                transparent 10px,
                transparent 20px
            );
        `,
        to_be_confirmed: css`
            background-color: ${theme.palette.neutral['white']};
            background-image: repeating-linear-gradient(
                45deg,
                ${theme.palette.neutral['50']} 0px,
                ${theme.palette.neutral['50']} 10px,
                transparent 10px,
                transparent 20px
            );
        `
    }
}

export const StyledAgendaAppointmentEvent = styled.div<{$status: AgendaEventStatus}>(
    ({theme, $status}) => css`
        display: grid;
        grid-template-columns: auto 1fr;
        gap: ${theme.spacing}px;
        height: 100%;
        padding: ${theme.spacing}px;
        border-radius: 8px;
        border: ${$status == 'tentative'
            ? `2px dashed ${theme.palette.neutral['200']}`
            : `1px solid ${theme.palette.neutral['200']}`};
        box-shadow: ${$status == 'tentative' ? 'none' : theme.shadows.sm};
        ${makeAgendaAppointmentBackground(theme)[$status]}
    `
)

export const StyledAgendaAppointmentEventContent = styled(Flexbox)`
    overflow: hidden;
`

export const StyledAgendaAppointmentEventTitle = styled.h4(
    ({theme: {typography, palette}}) => css`
        color: ${palette.neutral['950']};
        ${typography.textSm};
        font-weight: 500;
    `
)

export const StyledAgendaAppointmentEventParagraph = styled.div(
    ({theme: {typography, palette, spacing}}) => css`
        display: flex;
        align-items: flex-start;
        gap: ${spacing * 0.5}px;
        color: ${palette.neutral['600']};
        ${typography.textSm};
        font-weight: 400;
        white-space: nowrap;
        line-height: 18px;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;

        & > button {
            display: inline-flex;
            & > svg {
                transform: translateY(${spacing * 0.5}px);
                fill: ${palette.neutral['500']};
            }
        }

        & > span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    `
)

export const StyledAgendaAppointmentEventSubParagraph = styled(StyledAgendaAppointmentEventParagraph)(
    ({theme: {palette}}) => css`
        color: ${palette.neutral['500']};
        white-space: wrap;
    `
)

export const StyledAgendaAppointmentEventStatusIndicator = styled.div<{$status: AgendaEventStatus}>(
    ({$status}) => css`
        width: 3px;
        height: 100%;
        border-radius: 6px;
        background-color: ${agendaEventStatusMapToColor[$status]};
    `
)
